// ==============================================================
// Frontend models for Presales Project Management.
// Shared by the Presales Clients, Inventory, and Details pages.
// Mirrors the backend DTOs in ResourceAllocation.Application/DTOs.
// ==============================================================

export type SalesStage =
  | 'New Idea'
  | 'Lead'
  | 'Qualified'
  | 'Proposal'
  | 'Negotiation'
  | 'Won'
  | 'Lost';

/**
 * Pursuit progress — how work on the deal is tracking, independent of the pipeline
 * position in SalesStage.
 */
export type PresalesProgress =
  | 'To Start'
  | 'InProgress'
  | 'On Hold'
  | 'Closed';

/**
 * RAG health of the pursuit, independent of both SalesStage and PresalesProgress.
 * Mirrors the Status field on the main Project so both details pages read alike.
 */
export type PresalesProjectStatus = 'Green' | 'Amber' | 'Red';

export type Currency = 'LKR' | 'USD' | 'AUD' | 'EUR' | 'SGD' | 'GBP';

/**
 * A prospective client tracked while a deal is in pursuit. Lives in a
 * separate DB table from the main Clients — never appears in allocation /
 * timesheet workflows. Promoted to the main Clients table on Deal Won.
 */
export interface PresalesClient {
  id: string;
  name: string;
  description?: string;

  // Primary contact
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;

  // Categorization
  industry?: string;
  country?: string;
  companyWebsite?: string;
  leadSource?: string;

  notes?: string;
  isActive: boolean;

  // Set once this client has been promoted to the main Clients table
  promotedToClientId?: string | null;
  promotedToClientName?: string | null;
}

/** Payload for creating/updating a presales client. */
export interface CreatePresalesClient {
  name: string;
  description?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  industry?: string;
  country?: string;
  companyWebsite?: string;
  leadSource?: string;
  notes?: string;
  isActive?: boolean;
}

/**
 * A cost center used only while a deal is in pursuit. Separate DB table from the
 * main CostCenters — never appears in allocation grids or the admin cost-center
 * screen. Copied into the main CostCenters table on Deal Won.
 */
export interface PresalesCostCenter {
  id: string;
  name: string;
  description?: string;

  /** Owning prospective client. Null = SHARED — offered for every client. */
  presalesClientId?: string | null;
  presalesClientName?: string | null;

  serviceGroupId?: string | null;
  serviceGroupName?: string | null;

  isActive: boolean;

  // Set once this cost center has been promoted to the main CostCenters table
  promotedToCostCenterId?: string | null;
  promotedToCostCenterName?: string | null;
}

/** Payload for creating/updating a presales cost center. */
export interface CreatePresalesCostCenter {
  name: string;
  description?: string;
  /** Leave null to create a SHARED cost center visible for every client. */
  presalesClientId?: string | null;
  serviceGroupId?: string | null;
  isActive?: boolean;
}

/**
 * A sales opportunity being pursued. Separate from the main Projects table
 * so unclosed deals don't appear in allocations, timesheets, or delivery reports.
 */
export interface PresalesProject {
  id: string;
  projectCode?: string;
  name: string;

  // People (Employees)
  managerId: string;
  managerName?: string;

  accountManagerId?: string | null;
  accountManagerName?: string | null;

  // Client (PresalesClients table, not main Clients)
  presalesClientId?: string | null;
  presalesClientName?: string | null;

  // System master data
  serviceGroupId?: string | null;
  serviceGroupName?: string | null;

  costCenterId?: string | null;
  costCenterName?: string | null;

  // Presales KPIs
  revenueTarget: number;
  currency: Currency;
  winningPercentage: number;         // 0–100
  salesStage: SalesStage;
  progress?: PresalesProgress | null;  // null until a PM sets one
  projectStatus?: PresalesProjectStatus | null;  // RAG health; null until a PM sets one

  // Effort — planned is stored; actual/remaining are calculated by the backend from the
  // twin project's timesheet hours, so they are available at every sales stage.
  plannedEstimateDays?: number | null;
  actualEffortDays?: number;           // Calculated from backend
  remainingEffortDays?: number;        // Calculated from backend

  // Descriptive
  description?: string;
  scopeSummary?: string;
  teamMembers?: string;              // free-text list of people working the deal; no UI yet
  comments?: string;                 // free-text running commentary (editable on Details)
  leadSource?: string;
  competitor?: string;
  lostReason?: string;               // populated only when SalesStage === 'Lost'

  // Dates
  startDate: string;                 // ISO date (yyyy-MM-dd or full ISO)
  endDate?: string | null;
  expectedCloseDate?: string | null;
  actualCloseDate?: string | null;   // auto-stamped on Won/Lost transition

  isActive: boolean;

  // Set once this presales project has been promoted to the main Projects table
  promotedToProjectId?: string | null;
}

export interface CreatePresalesProject {
  projectCode?: string;
  name: string;
  managerId: string;
  accountManagerId?: string | null;
  presalesClientId?: string | null;
  serviceGroupId?: string | null;
  costCenterId?: string | null;
  revenueTarget: number;
  currency: Currency;
  winningPercentage: number;
  salesStage: SalesStage;
  progress?: PresalesProgress | null;
  projectStatus?: PresalesProjectStatus | null;
  plannedEstimateDays?: number | null;
  description?: string;
  scopeSummary?: string;
  teamMembers?: string;
  comments?: string;
  leadSource?: string;
  competitor?: string;
  lostReason?: string;
  startDate: string;
  endDate?: string | null;
  expectedCloseDate?: string | null;
  isActive?: boolean;
}

// ================= Currency conversion (USD KPI cards) =================

/**
 * Cached USD conversion rate for one currency, served by GET /api/CurrencyRate.
 * `rateToUsd` = value of 1 unit of `currency` in USD (USD itself = 1).
 * Rates are auto-refreshed on the backend from a free FX provider — read-only here.
 */
export interface CurrencyRate {
  id: string;
  currency: Currency;
  rateToUsd: number;
  lastRefreshedUtc: string;
}

/**
 * Converts an amount in its native currency to USD using the given rate map.
 * Returns null when no rate is known for that currency — callers should treat
 * a null as "cannot convert" and surface it rather than silently counting 0.
 */
export function toUsd(
  amount: number | null | undefined,
  currency: Currency,
  rates: Map<Currency, number>
): number | null {
  const rate = rates.get(currency);
  if (rate == null) return null;
  return (amount ?? 0) * rate;
}

/** Builds a quick lookup map from the CurrencyRate list returned by the API. */
export function buildRateMap(rates: CurrencyRate[] | null | undefined): Map<Currency, number> {
  const map = new Map<Currency, number>();
  for (const r of rates ?? []) map.set(r.currency, r.rateToUsd);
  return map;
}

// ================= Static option lists (matching backend + prototype) =================

export const SALES_STAGES: { label: string; value: SalesStage }[] = [
  { label: 'New Idea',    value: 'New Idea' },
  { label: 'Lead',        value: 'Lead' },
  { label: 'Qualified',   value: 'Qualified' },
  { label: 'Proposal',    value: 'Proposal' },
  { label: 'Negotiation', value: 'Negotiation' },
  { label: 'Won',         value: 'Won' },
  { label: 'Lost',        value: 'Lost' },
];

export const PRESALES_PROGRESS_OPTIONS: { label: string; value: PresalesProgress }[] = [
  { label: 'To Start',   value: 'To Start' },
  // Label only — the stored value stays 'InProgress' so existing rows keep working.
  { label: 'In Progress', value: 'InProgress' },
  { label: 'On Hold',    value: 'On Hold' },
  { label: 'Closed',     value: 'Closed' },
];

/**
 * Display label for a stored progress value. Stored values are unchanged for
 * backwards compatibility, so 'InProgress' has to be spaced for display.
 * Unknown/legacy values pass through as-is rather than being blanked.
 */
export function presalesProgressLabel(progress?: string | null): string {
  if (!progress) return '';
  return PRESALES_PROGRESS_OPTIONS.find(o => o.value === progress)?.label ?? progress;
}

/** RAG health options. Mirrors statusOptions in ProjectDetailsComponent. */
export const PRESALES_PROJECT_STATUS_OPTIONS: { label: string; value: PresalesProjectStatus }[] = [
  { label: 'Green', value: 'Green' },
  { label: 'Amber', value: 'Amber' },
  { label: 'Red',   value: 'Red' },
];

export const CURRENCIES: { label: string; value: Currency }[] = [
  { label: 'LKR  (Rs)',   value: 'LKR' },
  { label: 'USD  ($)',    value: 'USD' },
  { label: 'AUD  (A$)',   value: 'AUD' },
  { label: 'EUR  (€)',    value: 'EUR' },
  { label: 'SGD  (S$)',   value: 'SGD' },
  { label: 'GBP  (£)',    value: 'GBP' },
];

/** Free-text — kept as a list to power the dropdown but new values are allowed via API. */
export const LEAD_SOURCES: string[] = [
  'Referral',
  'LinkedIn outreach',
  'Event / conference',
  'Existing client — repeat business',
  'Cold outreach',
  'Inbound (client contacted us)',
  'Other',
];

export const INDUSTRIES: string[] = [
  'Banking',
  'Insurance',
  'Telecom',
  'Aviation',
  'Retail',
  'Manufacturing',
  'Healthcare',
  'Education',
  'Government',
  'Other',
];

export const COUNTRIES: string[] = [
  'Sri Lanka',
  'United States',
  'United Kingdom',
  'Australia',
  'United Arab Emirates',
  'Singapore',
  'India',
  'Other',
];

// ================= Promote-to-Delivery =================

/** Request payload for POST /api/PresalesProject/{id}/promote. */
export interface PromoteToDeliveryRequest {
  /** true = copy PresalesClient into main Clients. false = link to an existing main-Clients row. */
  createNewClient: boolean;
  /** Required when createNewClient = false. */
  existingClientId?: string | null;
  /** Optional override for the delivery project's name (default: presales name). */
  deliveryProjectName?: string;
  /** Optional override for the delivery project's code. */
  deliveryProjectCode?: string;
}

/** Response from POST /api/PresalesProject/{id}/promote. */
export interface PromoteResult {
  presalesProjectId: string;
  promotedToProjectId: string;
  promotedToClientId: string;
  message: string;
}

/**
 * A trackable piece of presales work — something with an owner, a due date and a state of
 * progress.
 *
 * Distinct from PresalesActivity, which is a past-tense log of a meeting or call. A task may
 * belong to a presales project or stand on its own, which is why presalesProjectId is
 * optional.
 */
export interface PresalesTask {
  id: string;
  presalesProjectId?: string | null;
  /** Resolved server-side so a board card can be labelled without a second call. */
  presalesProjectName?: string | null;
  /**
   * Client, Presale PM and Cost Center are DERIVED from the parent project, not stored on
   * the activity — they are read-only everywhere in the UI. Sending them back on a save
   * would have no effect.
   */
  clientName?: string | null;
  projectManagerName?: string | null;
  costCenterName?: string | null;
  name: string;
  description?: string | null;
  startDate?: string | null;
  /** Due date — labelled "End Date" in the UI. */
  targetDate?: string | null;
  /** The Sales Owner driving this activity. */
  ownerId?: string | null;
  ownerName?: string | null;
  /** Who is doing the work. Distinct from the Sales Owner above. */
  assignedToId?: string | null;
  assignedToName?: string | null;
  /** Same vocabulary as the project board's columns. */
  progress: PresalesProgress;
  /** How far along, 0–100. Separate from progress, which is a state. */
  progressPercentage: number;
  status?: PresalesProjectStatus | null;
  comments?: string | null;
  isActive: boolean;
  /** True when this activity can be staffed from the allocation screens. */
  isAllocatable?: boolean;
  /**
   * The twin Projects row allocations are booked against. Server-set — created the first
   * time the activity is marked allocatable, and never sent back on a save.
   */
  allocationProjectId?: string | null;
  createdDate?: string;
  updatedDate?: string | null;
}

/** Create/update payload. Server-set fields are deliberately absent. */
export interface CreatePresalesTask {
  presalesProjectId?: string | null;
  name: string;
  description?: string | null;
  startDate?: string | null;
  /** Due date — labelled "End Date" in the UI. */
  targetDate?: string | null;
  /** The Sales Owner driving this activity. */
  ownerId?: string | null;
  /** Who is doing the work. The name is resolved server-side. */
  assignedToId?: string | null;
  progress: PresalesProgress;
  progressPercentage: number;
  status?: PresalesProjectStatus | null;
  comments?: string | null;
  isActive: boolean;
  /**
   * Opt this activity into the allocation screens. The twin Projects row behind it is
   * created by the server, so no project id is sent.
   */
  isAllocatable?: boolean;
}

/**
 * One card on the tracker board. Projects and tasks are shown side by side, so the board
 * works against this shape rather than either type directly.
 */
export interface TrackerCard {
  kind: 'project' | 'task';
  id: string;
  name: string;
  /** Client for a project; the parent project's name for a task. */
  subtitle?: string | null;
  /**
   * The client, on an activity card only — derived from its parent project. A project card
   * already carries its client in `subtitle`, so this stays null there to avoid repeating it.
   */
  clientName?: string | null;
  /** Shown on the card, clamped to two lines, with the full text on hover. */
  description?: string | null;
  /** Running notes, shown beneath the description on the card. */
  comments?: string | null;
  /** Presale PM for a project, Sales Owner for an activity. */
  ownerName?: string | null;
  progress: PresalesProgress;
  status?: PresalesProjectStatus | null;
  /** Null on a project, which has no numeric progress of its own. */
  progressPercentage?: number | null;
  startDate?: string | null;
  /** Due date — labelled "End Date" in the UI. */
  targetDate?: string | null;
  /** The underlying record, for the edit dialog. */
  source: PresalesProject | PresalesTask;
}
